import { create } from 'zustand'
import { devtools } from 'zustand/middleware'

import { changeAvatar, changePassword, getProfile } from './api'
import { IProfileActions, IProfileState, ProfilePasswordFormType } from './state.types'
import { useLogin } from '@api/login/state'

let profileLoadVersion = 0
let avatarSaveVersion = 0
let avatarRevision = 0
let lastSavedAvatar: {loginName: string; avatarID: number; avatarUrl?: string | null} | undefined

const currentSession = () => {
    try { return localStorage.getItem('us_id') }
    catch { return null }
}

const isSameOperator = (loginName: string, session: string | null) => {
    const auth = useLogin.getState()
    return auth.isAuthorized && (loginName && auth.profile.loginName
        ? auth.profile.loginName === loginName : currentSession() === session)
}

export const initialStateProfile: IProfileState = {
    isLoadingProfile: false,
    isLoadingPassword: false,

    profile: {
        loginName: '',
        lastName: '',
        firstName: '',
        secondName: '',
        accessLevelID: 0,
        accessLevelName: '',
        isLocked: 0,
        itemsSites: [],
        itemsAccess: [],
    },
}

/** Хранилище страницы - Мой профиль */
export const useProfile = create<IProfileState & IProfileActions>()(devtools((set, get) => ({
    ...initialStateProfile,

    /** Сброс хранилища, до начального состояния */
    clearStateProfile: () => {
        profileLoadVersion += 1
        set(initialStateProfile)
    },

    /** Получение профиля текущего оператора */
    actionGetProfile: async () => {
        const version = ++profileLoadVersion
        const revision = avatarRevision
        const session = currentSession()
        set({ isLoadingProfile: true })

        try {
            const response = await getProfile()
            if (version === profileLoadVersion && response.status === 200 && response.data && isSameOperator(response.data.loginName, session)) {
                const preserveAvatar = revision !== avatarRevision && lastSavedAvatar?.loginName === response.data.loginName
                set({profile: preserveAvatar ? {...response.data, avatarID: lastSavedAvatar!.avatarID, avatarUrl: lastSavedAvatar!.avatarUrl} : response.data})
            }
            return response
        } finally {
            if (version === profileLoadVersion) set({isLoadingProfile: false})
        }
    },

    /** Сохраняет аватар и сразу обновляет шапку для того же оператора. */
    actionChangeAvatar: async (avatarID: number) => {
        const version = ++avatarSaveVersion
        const loginName = get().profile.loginName || useLogin.getState().profile.loginName
        const session = currentSession()
        const response = await changeAvatar(avatarID)
        if (response.status !== 200 || version !== avatarSaveVersion) return response

        if (!isSameOperator(loginName, session)) return response

        const savedAvatarID = response.data?.avatarID ?? avatarID
        if (!Number.isSafeInteger(savedAvatarID) || savedAvatarID < 1) {
            return {...response, status: 500, message: 'Сервер не подтвердил выбранный аватар.'}
        }
        avatarRevision += 1
        const avatarUrl = response.data?.avatarUrl ?? null
        lastSavedAvatar = {loginName, avatarID: savedAvatarID, avatarUrl}
        if (get().profile.loginName === loginName) set({profile: {...get().profile, avatarID: savedAvatarID, avatarUrl}})
        useLogin.setState(state => ({profile: {...state.profile, avatarID: savedAvatarID, avatarUrl}}))
        return response
    },

    /** Refreshes only the current operator's avatar after catalog changes. */
    actionRefreshAvatar: async () => {
        const session = currentSession()
        const loginName = useLogin.getState().profile.loginName || get().profile.loginName
        const revision = avatarRevision
        const response = await getProfile()
        if (response.status !== 200 || !response.data || !isSameOperator(loginName, session)
            || loginName && response.data.loginName !== loginName) return response
        if (revision !== avatarRevision) return response
        const {avatarID, avatarUrl = null} = response.data
        if (!Number.isSafeInteger(avatarID) || !avatarID || avatarID < 1) return response
        avatarRevision += 1
        lastSavedAvatar = {loginName: response.data.loginName, avatarID, avatarUrl}
        if (get().profile.loginName === response.data.loginName) set({profile: {...get().profile, avatarID, avatarUrl}})
        useLogin.setState(state => ({profile: {...state.profile, avatarID, avatarUrl}}))
        return response
    },

    /** Смена пароля текущего оператора */
    actionChangePassword: async (data: ProfilePasswordFormType) => {
        set({ isLoadingPassword: true })

        const response = await changePassword(data)

        set({ isLoadingPassword: false })

        return response
    },
}), { name: 'StateProfile' }))
